import type { TFunction } from 'i18next';

import type { MetaIntegration } from '@feathr/blackbox';
import type { TAlertType } from '@feathr/components';

/** Informational banner shown above the page body for certain status states. */
export interface IMetaInfoBanner {
  /** Short banner title. */
  title: string;
  /** Banner body copy. */
  message: string;
  /** Alert severity level. */
  type: TAlertType;
}

/**
 * Whether the integration has completed setup and is currently re-authenticating.
 *
 * @param integration - The current MetaIntegration model, or `null` when not loaded.
 */
export function getIsReconnecting(integration: MetaIntegration | null): boolean {
  return integration?.isConfiguring === true && Boolean(integration.setupCompletedAt);
}

/**
 * Whether to show the post-connection UI (settings button, connected view).
 * True for any working or near-working connection; false routes to onboarding.
 *
 * @param integration - The current MetaIntegration model, or `null` when not loaded.
 */
export function getShowPostConnectionUI(integration: MetaIntegration | null): boolean {
  return (
    integration?.isConnected === true ||
    integration?.isNoActivePages === true ||
    getIsReconnecting(integration)
  );
}

/**
 * Whether to show the status badge. Visible once an integration exists and
 * has progressed past not_connected.
 *
 * @param integration - The current MetaIntegration model, or `null` when not loaded.
 */
export function getShowBadge(integration: MetaIntegration | null): boolean {
  return integration !== null && !integration.isNotConnected;
}

/**
 * Page title — shorter form when connected, longer CTA when onboarding.
 *
 * @param showPostConnectionUI - Whether the post-connection UI is active.
 * @param t - Translation function.
 */
export function getPageTitle(showPostConnectionUI: boolean, t: TFunction): string {
  if (showPostConnectionUI) {
    return t('Meta Integration');
  }
  return t('Connect Meta for Paid Social Advertising');
}

/**
 * Page description — hidden when connected, shown during onboarding.
 *
 * @param showPostConnectionUI - Whether the post-connection UI is active.
 * @param t - Translation function.
 */
export function getPageDescription(
  showPostConnectionUI: boolean,
  t: TFunction,
): string | undefined {
  if (showPostConnectionUI) {
    return undefined;
  }
  return t(
    'Integrate your Facebook Business Manager and Instagram accounts to create, manage, and optimize paid social campaigns alongside your other marketing efforts.',
  );
}

/**
 * Derive the informational banner (if any) for the current integration state.
 *
 * Returns `undefined` when no banner should be shown. The page-top danger
 * `Alert` is handled separately via the hook's `error` field; this helper only
 * covers the warning-level banners (configuring sub-states and `no_active_pages`).
 *
 * @param integration - The current MetaIntegration model, or `null` when not loaded.
 * @param t - Translation function.
 * @returns A banner title/message pair, or `undefined` when no banner applies.
 */
export function getMetaInfoBanner(
  integration: MetaIntegration | null,
  t: TFunction,
): IMetaInfoBanner | undefined {
  if (!integration?.isConfiguring && !integration?.isNoActivePages) {
    return undefined;
  }

  if (integration.isConfiguring && !integration.setupCompletedAt) {
    return {
      title: t('Finishing setup'),
      message: t('Finishing setup — this usually takes a few minutes.'),
      type: 'warning',
    };
  }

  if (integration.isConfiguring) {
    return {
      title: t('Reconnecting'),
      message: t('Reconnecting to Meta — ads actions are temporarily paused.'),
      type: 'warning',
    };
  }

  return {
    title: t('No active pages'),
    message: t('No active Facebook or Instagram pages were found for this connection.'),
    type: 'info',
  };
}
